import * as T from 'three';

/** Deterministic value noise for terrain shaping and procedural textures. */
export function makeNoise(seed: number) {
  const s = (seed >>> 0) || 1;
  const hash = (x: number, z: number) => {
    let h = Math.imul(x | 0, 374761393) ^ Math.imul(z | 0, 668265263) ^ Math.imul(s, 2246822519);
    h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
  const value = (x: number, z: number) => {
    const ix = Math.floor(x), iz = Math.floor(z), fx = x - ix, fz = z - iz;
    const ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
    const a = hash(ix, iz), b = hash(ix + 1, iz), c = hash(ix, iz + 1), d = hash(ix + 1, iz + 1);
    return a + (b - a) * ux + (c - a) * uz + (a - b - c + d) * ux * uz;
  };
  const fbm = (x: number, z: number, octaves = 5) => {
    let sum = 0, amp = .5, norm = 0;
    for (let i = 0; i < octaves; i++) { sum += value(x, z) * amp; norm += amp; const nx = x * 1.6 + z * 1.2, nz = -x * 1.2 + z * 1.6; x = nx + 17.3; z = nz + 9.1; amp *= .5; }
    return sum / norm;
  };
  const ridged = (x: number, z: number, octaves = 5) => {
    let sum = 0, amp = .5, norm = 0, weight = 1;
    for (let i = 0; i < octaves; i++) {
      let n = 1 - Math.abs(value(x, z) * 2 - 1); n *= n * weight; weight = Math.min(1, n * 1.8);
      sum += n * amp; norm += amp; const nx = x * 1.9 + z * 1.1, nz = -x * 1.1 + z * 1.9; x = nx + 5.7; z = nz + 13.3; amp *= .5;
    }
    return sum / norm;
  };
  return { hash, value, fbm, ridged };
}

/** Tileable RGBA noise: r = broad, g = medium, b = fine, a = cellular-ish speckle. */
export function noiseTexture(size = 256, seed = 7) {
  const n = makeNoise(seed), data = new Uint8Array(size * size * 4);
  const tile = (x: number, z: number, period: number) => {
    // Tileable value noise by wrapping lattice coordinates.
    const ix = Math.floor(x), iz = Math.floor(z), fx = x - ix, fz = z - iz;
    const ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
    const w = (v: number) => ((v % period) + period) % period;
    const a = n.hash(w(ix), w(iz)), b = n.hash(w(ix + 1), w(iz)), c = n.hash(w(ix), w(iz + 1)), d = n.hash(w(ix + 1), w(iz + 1));
    return a + (b - a) * ux + (c - a) * uz + (a - b - c + d) * ux * uz;
  };
  const octaves = (x: number, z: number, base: number, count: number) => {
    let sum = 0, amp = .5, norm = 0, p = base;
    for (let i = 0; i < count; i++) { sum += tile(x * p / size, z * p / size, p) * amp; norm += amp; p *= 2; amp *= .5; }
    return sum / norm;
  };
  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    const i = (z * size + x) * 4;
    data[i] = octaves(x, z, 4, 4) * 255; data[i + 1] = octaves(x, z, 16, 3) * 255; data[i + 2] = octaves(x, z, 64, 2) * 255; data[i + 3] = tile(x * 32 / size, z * 32 / size, 32) * 255;
  }
  const texture = new T.DataTexture(data, size, size, T.RGBAFormat);
  texture.wrapS = texture.wrapT = T.RepeatWrapping; texture.magFilter = T.LinearFilter; texture.minFilter = T.LinearMipmapLinearFilter; texture.generateMipmaps = true; texture.needsUpdate = true;
  return texture;
}
